/**
 * 故事板相关服务：AI 生成分镜（文本生成）、分镜解析
 * 支持火山引擎 / 钱多多（OpenAI 兼容直连）与阿里（后端 DashScope 代理）
 */

import { alibabaAI, modelAPI } from './api';

export interface TextModelConfig {
  id: string;
  name: string;
  type: string;
  provider: string;
  api_key: string;
  base_url?: string;
  model_name: string;
  is_active: boolean;
}

export interface StoryboardScene {
  duration: number;
  prompt: string;
}

const DEFAULT_BASE_URL = 'https://ark.cn-beijing.volces.com/api/v3';

/** 从后台获取当前启用的文本/对话模型（按启用供应商，不写死某一家） */
export async function getTextModelConfig(): Promise<TextModelConfig | null> {
  try {
    const models = await modelAPI.getAllModels();
    const textModels = models
      .filter(model => model.is_active && (model.type === 'text' || model.type === 'chat'))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return textModels[0] as TextModelConfig | null;
  } catch (error) {
    console.error('获取文本模型配置失败:', error);
    return null;
  }
}

/** 调用文本模型进行分镜扩充生成，解析为分镜列表 */
export async function generateStoryboardScenesFromText(
  userInput: string,
  textConfig: TextModelConfig | null
): Promise<StoryboardScene[]> {
  const trimmed = userInput.trim();
  if (!trimmed) return [];

  if (!textConfig?.model_name) {
    throw new Error('请先在后台配置并启用文本/对话模型，才能使用 AI 分镜扩充');
  }
  if (textConfig.provider !== 'alibaba' && !textConfig.api_key) {
    throw new Error('请先在后台配置文本模型的 API Key');
  }

  const systemPrompt = `你是一个视频分镜脚本助手。根据用户输入的故事或脚本，完成两件事：
1. 拆分为多个（至少2个）视频分镜，每个分镜一行。
2. 对每个分镜进行扩写：把简短梗概扩写成更详细、更丰富的镜头描述，便于视频生成。扩写时可增加画面细节、光影氛围、人物动作、景别（如特写/中景/全景）等，使描述更具画面感和可拍性，但每条保持 1–3 句话，不要冗长。

输出格式：每行一条分镜，格式为 数字(时长秒数)\\t扩写后的分镜描述。数字和描述之间用 Tab 或空格分隔。
示例（必须多行，描述为扩写后的效果）：
3	阳光洒在公园草坪上，几名小朋友在树荫下追逐嬉戏，远处有家长坐在长椅上聊天，画面温暖明亮。
4	会议室里几位同事围坐桌旁，有人指着屏幕讲解方案，其他人专注倾听或点头，窗外是城市天际线。
3	众人纷纷鼓掌，有人站起握手，面带笑容，会议在轻松氛围中结束。
注意：必须输出多行，每行一个分镜；每条描述必须是扩写后的详细镜头描述，不要只复述用户原文。时长建议 2-5 秒。只输出分镜行，不要其他解释。`;

  try {
    let content: string | undefined;

    if (textConfig.provider === 'alibaba') {
      const data = await alibabaAI.chat(
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: trimmed },
        ],
        { max_tokens: 2048, model_id: textConfig.id }
      );
      content = data?.choices?.[0]?.message?.content?.trim();
    } else {
      const baseUrl = (textConfig.base_url || DEFAULT_BASE_URL).replace(/\/$/, '');
      const url = `${baseUrl}/chat/completions`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${textConfig.api_key}`,
        },
        body: JSON.stringify({
          model: textConfig.model_name,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: trimmed },
          ],
          max_tokens: 2048,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: { message?: string } }).error?.message || `请求失败 ${response.status}`);
      }

      const data = (await response.json()) as {
        choices?: Array<{ message?: { content?: string } }>;
      };
      content = data.choices?.[0]?.message?.content?.trim();
    }

    if (content) {
      const parsed = parseStoryboardLines(content);
      if (parsed.length > 0) return parsed;
    }
    throw new Error('模型返回内容为空，请重试或换一种描述');
  } catch (e) {
    if (e instanceof Error) throw e;
    throw new Error('文本模型调用失败，请检查网络与模型配置');
  }
}

/** 解析分镜文本 */
function parseStoryboardLines(text: string): StoryboardScene[] {
  let lines = text
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];

  if (lines.length === 1 && /[。；]/.test(lines[0])) {
    lines = lines[0]
      .split(/[。；]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  const scenes: StoryboardScene[] = [];
  for (const line of lines) {
    const tabMatch = line.match(/^(\d+)\s*[\t]\s*(.+)$/);
    const spaceMatch = line.match(/^(\d+)\s+(.+)$/);
    if (tabMatch) {
      scenes.push({
        duration: Math.min(10, Math.max(1, parseInt(tabMatch[1], 10) || 3)),
        prompt: tabMatch[2].trim(),
      });
    } else if (spaceMatch) {
      scenes.push({
        duration: Math.min(10, Math.max(1, parseInt(spaceMatch[1], 10) || 3)),
        prompt: spaceMatch[2].trim(),
      });
    } else {
      const prompt = line.trim();
      if (prompt) scenes.push({ duration: 3, prompt });
    }
  }
  return scenes;
}
